import { useEffect, useRef } from 'react';

import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { checkSessionHealth } from '@/renderer/lib/auth/sessionHealth';
import { pushCurrentSessionToMain } from '@/renderer/lib/powersync/ensureOutletSync';
import {
  resolveSupabaseKey,
  resolveSupabaseUrl,
  supabase,
} from '@/renderer/services/supabase';
import { isElectron } from '@/renderer/util';

const CHECK_INTERVAL_MS = 60_000;
const REACHABILITY_TIMEOUT_MS = 5_000;

// Any HTTP response at all (even an error status) proves the auth server is
// reachable — only a network failure or timeout means "offline".
async function isAuthServerReachable(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REACHABILITY_TIMEOUT_MS);
  try {
    await fetch(`${resolveSupabaseUrl()}/auth/v1/health`, {
      headers: { apikey: resolveSupabaseKey() },
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Makes sure a signed-in user can never sit on a dead session. While someone
 * is signed in, periodically (and whenever the network comes back, or main
 * reports it has no usable PowerSync token) checks the Supabase session:
 *
 *  - rejected by the server, or missing while online → `onExpired()` (sign out
 *    and return to the login screen);
 *  - valid, and main asked for it → re-relay it so PowerSync can reconnect;
 *  - offline / undeterminable → do nothing; the POS keeps working offline.
 */
export function useSessionExpiryGuard(onExpired: () => void): void {
  const isSignedIn = Boolean(useAppSelector(selectCurrentUser));
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    if (!isSignedIn) return undefined;

    let cancelled = false;
    let inFlight = false;

    const check = async (requestedByMain: boolean) => {
      if (inFlight) return;
      inFlight = true;
      try {
        const health = await checkSessionHealth(
          supabase.auth,
          isAuthServerReachable,
        );
        if (cancelled) return;
        if (health === 'expired') {
          onExpiredRef.current();
        } else if (health === 'valid' && requestedByMain) {
          await pushCurrentSessionToMain();
        }
        // 'refreshed' relays itself via TOKEN_REFRESHED (App.tsx).
      } catch (err) {
        console.error('[Auth] Session check failed:', err);
      } finally {
        inFlight = false;
      }
    };

    const handleOnline = () => {
      check(false);
    };
    const interval = setInterval(() => check(false), CHECK_INTERVAL_MS);
    window.addEventListener('online', handleOnline);
    const unsubscribeMain = isElectron()
      ? window.electron.auth.onSessionRequired(() => {
          check(true);
        })
      : undefined;
    check(false);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      unsubscribeMain?.();
    };
  }, [isSignedIn]);
}
